import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  Filter,
  Calendar,
  Tag,
  Download,
  Upload,
  Trash2,
  Eye,
  MessageSquare,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Check,
  X,
  FileText,
  Hash,
  Clock,
  Info,
  AlertCircle,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { ChatSession, ProviderType } from '@/types';
import { DataExportUtil } from '@/utils/dataExport';
import { DataImportUtil } from '@/utils/dataImport';

// 供应商类型配置
const providerTypeConfig = {
  [ProviderType.OPENAI]: {
    name: 'OpenAI',
    color: 'bg-green-100 text-green-800',
    icon: '🤖',
  },
  [ProviderType.ANTHROPIC]: {
    name: 'Anthropic',
    color: 'bg-purple-100 text-purple-800',
    icon: '👤',
  },
  [ProviderType.GOOGLE_AI]: {
    name: 'Google AI',
    color: 'bg-blue-100 text-blue-800',
    icon: '🔍',
  },
  [ProviderType.AZURE_OPENAI]: {
    name: 'Azure OpenAI',
    color: 'bg-cyan-100 text-cyan-800',
    icon: '☁️',
  },
  [ProviderType.CUSTOM]: {
    name: '自定义',
    color: 'bg-gray-100 text-gray-800',
    icon: '⚙️',
  },
};

export const HistoryManagerPage: React.FC = () => {
  const { 
    providers, 
    sessions, 
    searchSessions, 
    getSessionStats,
    deleteSessions,
    importSessions,
    updateSessionTags,
    updateSessionSummary,
    setCurrentSession,
    setActiveTab,
    getStorageAnalysis,
    optimizeStorage,
    compressSessions,
    cleanupOldData,
  } = useAppStore();
  
  // 搜索和过滤状态
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // 批量操作状态
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [showStats, setShowStats] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState<string>('');
  const [importPreview, setImportPreview] = useState<any>(null);
  
  // 统计和优化信息
  const [stats, setStats] = useState<any>(null);
  const [storageAnalysis, setStorageAnalysis] = useState<any>(null);
  const [optimizationResult, setOptimizationResult] = useState<any>(null);
  
  // 所有标签列表
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    sessions.forEach(session => {
      session.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [sessions]);
  
  // 执行搜索
  const searchResults = useMemo(() => {
    return searchSessions({
      query: searchQuery,
      providerId: selectedProvider === 'all' ? undefined : selectedProvider,
      startDate,
      endDate,
      tags: selectedTags,
      page,
      pageSize,
    });
  }, [searchSessions, searchQuery, selectedProvider, startDate, endDate, selectedTags, page, pageSize]);
  
  // 加载统计和优化信息
  useEffect(() => {
    setStats(getSessionStats());
    setStorageAnalysis(getStorageAnalysis());
    setOptimizationResult(optimizeStorage());
  }, [sessions, getSessionStats, getStorageAnalysis, optimizeStorage]);
  
  // 处理日期格式化
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // 处理时间差格式化
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) return `${diffMins}分钟前`;
    if (diffHours < 24) return `${diffHours}小时前`;
    if (diffDays < 7) return `${diffDays}天前`;
    return date.toLocaleDateString('zh-CN');
  };
  
  // 处理会话选择
  const handleSessionSelect = (sessionId: string) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId) 
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };
  
  // 处理批量删除
  const handleBatchDelete = () => {
    if (selectedSessions.length === 0) return;
    
    const confirmMessage = selectedSessions.length === 1 
      ? '确定要删除选中的对话吗？此操作不可撤销。'
      : `确定要删除选中的 ${selectedSessions.length} 个对话吗？此操作不可撤销。`;
    
    if (confirm(confirmMessage)) {
      deleteSessions(selectedSessions);
      setSelectedSessions([]);
    }
  };
  
  // 处理批量导出
  const handleBatchExport = () => {
    const { providers, sessions, templates } = useAppStore.getState();
    
    DataExportUtil.exportToFile(
      providers,
      selectedSessions.length > 0 
        ? sessions.filter(session => selectedSessions.includes(session.id))
        : sessions,
      templates,
      `api-conversations-${new Date().toISOString().split('T')[0]}.json`,
      {
        includeProviders: true,
        includeSessions: true,
        includeTemplates: true,
        sessionIds: selectedSessions.length > 0 ? selectedSessions : undefined,
      }
    );
  };
  
  // 处理导入
  const handleImport = () => {
    try {
      // 验证和清理数据
      const validation = DataImportUtil.validateImportData(JSON.parse(importData));
      
      if (!validation.valid) {
        alert(`导入数据验证失败:\n${validation.errors.join('\n')}`);
        return;
      }
      
      // 显示警告
      if (validation.warnings.length > 0) {
        const proceed = confirm(`导入警告:\n${validation.warnings.join('\n')}\n\n是否继续导入？`);
        if (!proceed) return;
      }
      
      // 解析数据
      const rawData = JSON.parse(importData);
      const cleanedData = DataImportUtil.cleanImportData(rawData);
      
      // 分析数据
      const analysis = DataImportUtil.analyzeImportData(cleanedData);
      
      // 确认导入
      const confirmMessage = `将要导入:\n- 供应商: ${analysis.providers.count} 个\n- 会话: ${analysis.sessions.count} 个\n- 模板: ${analysis.templates.count} 个\n\n预计导入时间: ${analysis.estimatedImportTime}\n\n是否确认导入？`;
      
      if (!confirm(confirmMessage)) return;
      
      // 执行导入
      const result = importSessions(cleanedData);
      
      if (result.success) {
        // 生成导入报告
        const report = DataImportUtil.generateImportReport(cleanedData, result);
        const reportBlob = new Blob([report], { type: 'text/plain' });
        const reportUrl = URL.createObjectURL(reportBlob);
        
        alert(`导入成功！\n供应商: ${result.importedCount.providers}\n会话: ${result.importedCount.sessions}\n模板: ${result.importedCount.templates}\n\n导入报告已生成，是否下载？`);
        
        const downloadReport = confirm('是否下载导入报告？');
        if (downloadReport) {
          const link = document.createElement('a');
          link.href = reportUrl;
          link.download = `import-report-${new Date().toISOString().split('T')[0]}.txt`;
          link.click();
        }
        
        URL.revokeObjectURL(reportUrl);
        setShowImportModal(false);
        setImportData('');
      } else {
        alert(`导入失败:\n${result.errors.join('\n')}`);
      }
      
    } catch (error) {
      alert(`导入失败: ${error instanceof Error ? error.message : 'JSON 格式无效'}`);
    }
  };
  
  // 查看会话详情
  const handleViewSession = (session: ChatSession) => {
    setCurrentSession(session.id);
    setActiveTab('chat');
  };
  
  // 更新标签
  const handleUpdateTags = (sessionId: string, currentTags: string[]) => {
    const newTags = prompt('请输入新的标签（用逗号分隔）:', currentTags.join(', '));
    if (newTags !== null) {
      const tags = newTags.split(',').map(tag => tag.trim()).filter(tag => tag);
      updateSessionTags(sessionId, tags);
    }
  };
  
  // 更新摘要
  const handleUpdateSummary = (sessionId: string, currentSummary?: string) => {
    const newSummary = prompt('请输入新的摘要:', currentSummary || '');
    if (newSummary !== null) {
      updateSessionSummary(sessionId, newSummary);
    }
  };
  
  // 清空筛选条件
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedProvider('all');
    setStartDate('');
    setEndDate('');
    setSelectedTags([]);
    setPage(1);
  };
  
  return (
    <div className="space-y-6">
      {/* 头部操作栏 */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">对话历史管理</h3>
            <p className="text-sm text-gray-500 mt-1">
              管理您的对话历史记录，支持搜索、过滤、批量操作和统计分析
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowStats(!showStats)}
              className="btn-secondary flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              统计
            </button>
            <button
              onClick={() => setShowOptimization(!showOptimization)}
              className="btn-secondary flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              优化
            </button>
            <button
              onClick={handleBatchExport}
              className="btn-secondary flex items-center gap-2"
              disabled={sessions.length === 0}
            >
              <Download className="w-4 h-4" />
              导出
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              导入
            </button>
            <button
              onClick={handleBatchDelete}
              className="btn-danger flex items-center gap-2"
              disabled={selectedSessions.length === 0}
            >
              <Trash2 className="w-4 h-4" />
              删除选中
            </button>
          </div>
        </div>
      </div>
      
      {/* 统计信息面板 */}
      {showStats && stats && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">对话统计</h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const { providers, sessions, templates } = useAppStore.getState();
                  const report = DataExportUtil.generateExportReport(providers, sessions, templates);
                  const blob = new Blob([report], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `conversation-stats-${new Date().toISOString().split('T')[0]}.txt`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
                className="btn-secondary btn-sm flex items-center gap-1"
                title="导出统计报告"
              >
                <FileText className="w-4 h-4" />
                导出报告
              </button>
              <button
                onClick={() => setShowStats(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="text-2xl font-bold text-gray-900">{stats.totalSessions}</div>
              <div className="text-sm text-gray-500">总对话数</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold text-green-600">{stats.totalMessages}</div>
              <div className="text-sm text-gray-500">总消息数</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold text-blue-600">{stats.totalTokens}</div>
              <div className="text-sm text-gray-500">总令牌数</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold text-purple-600">
                {Object.keys(stats.byProvider).length}
              </div>
              <div className="text-sm text-gray-500">使用供应商数</div>
            </div>
          </div>
          
          {/* 供应商分布 */}
          {Object.keys(stats.byProvider).length > 0 && (
            <div className="mt-6">
              <h5 className="font-medium text-gray-700 mb-3">供应商分布</h5>
              <div className="space-y-2">
                {Object.entries(stats.byProvider).map(([provider, count]) => (
                  <div key={provider} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{provider}</span>
                    <span className="text-sm font-medium text-gray-900">{count as number} 个对话</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 数据备份建议 */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h5 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              数据备份建议
            </h5>
            {(() => {
              const { providers, sessions, templates } = useAppStore.getState();
              const backupRec = DataExportUtil.getBackupRecommendation(providers, sessions, templates);
              const sizeInfo = DataExportUtil.getExportSizeInfo(providers, sessions, templates);
              
              return (
                <div className={`p-4 rounded-lg ${backupRec.recommended ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-start gap-3">
                    {backupRec.recommended ? (
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${backupRec.recommended ? 'text-yellow-800' : 'text-gray-700'}`}>
                        {backupRec.reason}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {backupRec.suggestion}
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        预估导出大小: {sizeInfo.estimatedSize}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      
      {/* 性能优化面板 */}
      {showOptimization && storageAnalysis && optimizationResult && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">存储性能优化</h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const result = compressSessions();
                  alert(`压缩完成！\n原始大小: ${result.originalSize}\n压缩后: ${result.compressedSize}\n节省空间: ${result.estimatedSavings}`);
                }}
                className="btn-secondary btn-sm flex items-center gap-1"
                title="压缩存储数据"
              >
                <Filter className="w-4 h-4" />
                压缩数据
              </button>
              <button
                onClick={() => setShowOptimization(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="stat-card">
              <div className="text-2xl font-bold text-gray-900">{storageAnalysis.totalSessions}</div>
              <div className="text-sm text-gray-500">总对话数</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold text-green-600">{storageAnalysis.totalMessages}</div>
              <div className="text-sm text-gray-500">总消息数</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold text-blue-600">{storageAnalysis.storageSize}</div>
              <div className="text-sm text-gray-500">存储大小</div>
            </div>
          </div>
          
          {/* 优化建议 */}
          <div className="mb-6">
            <h5 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              优化建议
            </h5>
            <div className="space-y-2">
              {optimizationResult.recommendations.map((recommendation: string, index: number) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-blue-800">{recommendation}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* 优化操作 */}
          <div>
            <h5 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              优化操作
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {optimizationResult.actions.map((action: string, index: number) => (
                <button
                  key={index}
                  onClick={() => {
                    if (action === '立即压缩存储数据') {
                      const result = compressSessions();
                      alert(`压缩完成！节省空间: ${result.estimatedSavings}`);
                    } else if (action === '设置自动清理策略') {
                      const days = prompt('设置自动清理天数（默认90天）:', '90');
                      if (days) {
                        const result = cleanupOldData({ maxAgeDays: parseInt(days) });
                        alert(`清理完成！保留 ${result.keptCount} 个对话，删除 ${result.removedCount} 个旧对话`);
                      }
                    } else if (action === '启用自动压缩存储') {
                      alert('已启用自动压缩存储功能');
                    } else if (action === '启用消息懒加载功能') {
                      alert('已启用消息懒加载功能');
                    }
                  }}
                  className="btn-secondary text-left p-3 hover:bg-gray-100 transition-colors"
                >
                  <div className="font-medium text-gray-900">{action}</div>
                  <div className="text-xs text-gray-500 mt-1">点击执行此优化操作</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* 状态指示器 */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-700">优化级别</div>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                  storageAnalysis.optimizationLevel === 'high' 
                    ? 'bg-red-100 text-red-800' 
                    : storageAnalysis.optimizationLevel === 'medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {storageAnalysis.optimizationLevel === 'high' ? '高' : 
                   storageAnalysis.optimizationLevel === 'medium' ? '中' : '低'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-700">缓存状态</div>
                <div className="text-sm text-gray-600 mt-1">{storageAnalysis.cacheStatus}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 搜索和过滤面板 */}
      <div className="card">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* 搜索框 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              搜索对话
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="按名称、内容或摘要搜索..."
                className="input pl-10"
              />
            </div>
          </div>
          
          {/* 供应商过滤 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              供应商
            </label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="input"
            >
              <option value="all">全部供应商</option>
              {providers.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* 日期范围 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              开始日期
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              结束日期
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </div>
        </div>
        
        {/* 标签过滤 */}
        {allTags.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              标签过滤
            </label>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTags(prev => 
                    prev.includes(tag) 
                      ? prev.filter(t => t !== tag)
                      : [...prev, tag]
                  )}
                  className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                    selectedTags.includes(tag)
                      ? 'bg-primary-100 text-primary-800 border border-primary-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                  {selectedTags.includes(tag) && <Check className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* 操作按钮 */}
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-500">
            找到 {searchResults.pagination.total} 个对话
            {selectedSessions.length > 0 && `，选中 ${selectedSessions.length} 个`}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClearFilters}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              清空筛选
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">每页显示:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="text-sm border rounded px-2 py-1"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* 对话列表 */}
      <div className="card">
        {searchResults.items.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无对话记录</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || selectedProvider !== 'all' || startDate || endDate || selectedTags.length > 0
                ? '没有找到匹配的对话记录，请调整搜索条件'
                : '开始进行对话测试以创建历史记录'}
            </p>
            {!searchQuery && selectedProvider === 'all' && !startDate && !endDate && selectedTags.length === 0 && (
              <button
                onClick={() => setActiveTab('chat')}
                className="btn-primary"
              >
                前往对话测试
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {searchResults.items.map(session => {
              const provider = providers.find(p => p.id === session.providerId);
              const providerConfig = provider ? providerTypeConfig[provider.providerType] : providerTypeConfig[ProviderType.CUSTOM];
              
              return (
                <div
                  key={session.id}
                  className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                    selectedSessions.includes(session.id) ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    {/* 选择框和基本信息 */}
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedSessions.includes(session.id)}
                        onChange={() => handleSessionSelect(session.id)}
                        className="mt-1"
                      />
                      
                      {/* 会话内容 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-gray-900 truncate">
                            {session.name}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${providerConfig.color}`}>
                            {providerConfig.icon} {provider?.name || '未知供应商'}
                          </span>
                          <span className="text-xs text-gray-500">
                            模型: {session.model}
                          </span>
                        </div>
                        
                        {/* 摘要 */}
                        {session.summary && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {session.summary}
                          </p>
                        )}
                        
                        {/* 最后消息预览 */}
                        {session.messages.length > 0 && (
                          <div className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                            <span className="truncate">
                              {session.messages[session.messages.length - 1].content.substring(0, 100)}
                              {session.messages[session.messages.length - 1].content.length > 100 && '...'}
                            </span>
                          </div>
                        )}
                        
                        {/* 标签 */}
                        {session.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {session.tags.map(tag => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* 统计信息 */}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            <span>{session.messageCount} 条消息</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            <span>{session.tokenCount} 令牌</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatTimeAgo(session.lastMessageAt)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(session.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 操作按钮 */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewSession(session)}
                        className="btn-secondary btn-sm flex items-center gap-1"
                        title="查看对话"
                      >
                        <Eye className="w-3 h-3" />
                        查看
                      </button>
                      <div className="relative">
                        <button className="btn-secondary btn-sm p-1">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 hidden group-hover:block">
                          <button
                            onClick={() => handleUpdateTags(session.id, session.tags)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            编辑标签
                          </button>
                          <button
                            onClick={() => handleUpdateSummary(session.id, session.summary)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            编辑摘要
                          </button>
                          <div className="border-t border-gray-200 my-1"></div>
                          <button
                            onClick={() => handleSessionSelect(session.id)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {selectedSessions.includes(session.id) ? '取消选择' : '选择'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* 分页控件 */}
            {searchResults.pagination.total > pageSize && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  显示第 {(page - 1) * pageSize + 1} 到 {Math.min(page * pageSize, searchResults.pagination.total)} 条，
                  共 {searchResults.pagination.total} 条
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                    disabled={page === 1}
                    className="btn-secondary btn-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    上一页
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, Math.ceil(searchResults.pagination.total / pageSize)) }, (_, i) => {
                      const pageNum = i + 1;
                      const maxPage = Math.ceil(searchResults.pagination.total / pageSize);
                      let displayPage = pageNum;
                      
                      // 处理大量页面时的显示逻辑
                      if (maxPage > 5) {
                        if (page <= 3) {
                          displayPage = pageNum;
                        } else if (page >= maxPage - 2) {
                          displayPage = maxPage - 4 + pageNum;
                        } else {
                          displayPage = page - 2 + pageNum;
                        }
                      }
                      
                      return (
                        <button
                          key={displayPage}
                          onClick={() => setPage(displayPage)}
                          className={`w-8 h-8 rounded text-sm ${
                            page === displayPage
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {displayPage}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage(prev => Math.min(Math.ceil(searchResults.pagination.total / pageSize), prev + 1))}
                    disabled={page * pageSize >= searchResults.pagination.total}
                    className="btn-secondary btn-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* 导入模态框 */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">导入对话数据</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    导入数据 (JSON 格式)
                  </label>
                  <textarea
                    value={importData}
                    onChange={(e) => {
                      setImportData(e.target.value);
                      // 尝试预览数据
                      try {
                        const data = JSON.parse(e.target.value);
                        const analysis = DataImportUtil.analyzeImportData(data);
                        setImportPreview(analysis);
                      } catch {
                        setImportPreview(null);
                      }
                    }}
                    placeholder="粘贴导出的 JSON 数据..."
                    className="input font-mono text-sm h-48"
                    rows={8}
                  />
                  
                  {/* 数据预览 */}
                  {importPreview && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h5 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        数据预览
                      </h5>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-gray-900">{importPreview.providers.count}</div>
                          <div className="text-gray-600">供应商</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-gray-900">{importPreview.sessions.count}</div>
                          <div className="text-gray-600">会话</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-gray-900">{importPreview.templates.count}</div>
                          <div className="text-gray-600">模板</div>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        文件大小: {importPreview.totalSize} • 预计导入时间: {importPreview.estimatedImportTime}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">注意事项</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• 仅支持从本系统导出的 JSON 格式数据</li>
                    <li>• 导入时会自动跳过已存在的数据</li>
                    <li>• 建议先备份当前数据再进行导入操作</li>
                    <li>• 导入操作不可撤销，请谨慎操作</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportData('');
                    setImportPreview(null);
                  }}
                  className="btn-secondary"
                >
                  取消
                </button>
                <button
                  onClick={handleImport}
                  disabled={!importData.trim()}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  确认导入
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 样式组件
const statCardStyle = "p-4 bg-gray-50 rounded-lg border border-gray-200";
const HistoryManagerPageStyles = `
  .stat-card {
    ${statCardStyle}
  }
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .group:hover .group-hover\\:block {
    display: block;
  }
`;

// 注入样式
const styleSheet = document.createElement('style');
styleSheet.textContent = HistoryManagerPageStyles;
document.head.appendChild(styleSheet);